import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { PublicItem } from "../PublicItem/PublicItem";
import { ItemCardType } from "../../types/types";
import { gaEvent, toGaItem } from "../../analytics";

/** Used when no caller names the list, so `item_list_name` is never absent from a report. */
export const DEFAULT_LIST_NAME = "catalog";

interface ProductListProps {
  products: ItemCardType[];
  /**
   * Changing this collapses the grid back to the first batch.
   *
   * Deliberately driven by the *filter inputs* rather than by the identity of `products`: adding a
   * favourite invalidates the favourites query, which rebuilds the filtered array, and resetting on
   * that would throw away the scroll position of anyone who tapped a heart deep in the catalog.
   */
  resetKey?: string;
  /** GA4 `item_list_name` — what this grid is showing (EB-558). */
  listName?: string;
}

// How many cards to add per batch. At ~21 DOM nodes per card, 48 keeps the first paint near
// 1,000 nodes instead of the ~58,000 that rendering all ~2,700 synced products would cost
// (docs/catalog-modernization/README.md §6.4).
export const PAGE_SIZE = 48;

// Start fetching the next batch before the sentinel is actually on screen, so the grid stays
// ahead of the scroll instead of stuttering at the bottom.
const PREFETCH_MARGIN = "400px";

/**
 * The grid the results live in, exported so `ProductGridSkeleton` lays its placeholders out on the
 * same lines (EB-543). Shared rather than copied: a column count that differs between the loading
 * state and the loaded one produces exactly the jump the skeleton exists to prevent.
 */
export const PRODUCT_GRID_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr 1fr",
    md: "1fr 1fr 1fr 1fr",
  },
  gap: 2,
  alignItems: "stretch",
} as const;

const ProductListComponent: React.FC<ProductListProps> = ({
  products,
  resetKey,
  listName = DEFAULT_LIST_NAME,
}) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const reportedRef = useRef(0);

  /**
   * A new search term or category is a fresh result set, so start at the top of it rather than deep
   * inside the previous one.
   *
   * Adjusted during render rather than in an effect (the pattern React documents for state derived
   * from props) because `view_item_list` below reports whatever `visibleCount` says is on screen.
   * From an effect, the reset lands one commit late: the impression event would fire once for the
   * *old* count against the *new* results — reporting up to 2,700 impressions for a search showing
   * 48 — and then find itself already past the real first batch.
   */
  const [trackedResetKey, setTrackedResetKey] = useState(resetKey);
  if (trackedResetKey !== resetKey) {
    setTrackedResetKey(resetKey);
    setVisibleCount(PAGE_SIZE);
    reportedRef.current = 0;
  }

  const hasMore = visibleCount < products.length;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;

    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          // `isIntersecting` alone leaves the grid stuck. A jump to the bottom — dragging the
          // scrollbar, the End key, or a restored scroll position on back-navigation — can land
          // past the sentinel without ever intersecting it, and the observer then reports only
          // "not visible". The user sits at the end of the page and nothing more loads.
          //
          // A negative `top` means the sentinel is above the viewport, i.e. already scrolled
          // past, which is just as much a reason to extend as touching it. Each batch pushes the
          // sentinel back down below the fold, so this settles after a step or two instead of
          // running away.
          if (entry.isIntersecting || entry.boundingClientRect.top <= 0) {
            setVisibleCount((count) => Math.min(count + PAGE_SIZE, products.length));
          }
        }),
      { rootMargin: PREFETCH_MARGIN },
    );
    io.observe(el);

    return () => io.disconnect();
    // Re-observing after each batch is what lets a tall viewport fill itself: the sentinel is
    // still within the prefetch margin, so the fresh observer fires again immediately.
  }, [hasMore, visibleCount, products.length]);

  /**
   * One `view_item_list` per batch that reaches the screen (EB-558).
   *
   * Impressions here are genuinely partial — the grid renders 48 of a possible 2,700 — so they
   * cannot be inferred from the result count, which is the whole reason this event is worth
   * sending. `reportedRef` holds how far the list has been reported, so a batch is never counted
   * twice: `products` gets a new identity every time a favourite is toggled, and without the guard
   * each heart tap would re-report every impression above it.
   */
  useEffect(() => {
    const end = Math.min(visibleCount, products.length);
    const start = reportedRef.current;
    if (end <= start) return;

    reportedRef.current = end;
    gaEvent("view_item_list", {
      item_list_name: listName,
      items: products
        .slice(start, end)
        .map((item, offset) => toGaItem(item, { index: start + offset })),
    });
  }, [visibleCount, products, listName]);

  return (
    <Box>
      <Box sx={PRODUCT_GRID_SX}>
        {products.slice(0, visibleCount).map((item, index) => (
          <Box key={item.id}>
            <PublicItem item={item} listName={listName} index={index} />
          </Box>
        ))}
      </Box>

      {/* Only rendered while more remain, so the observer can't keep firing at the end of the list. */}
      {hasMore && <Box ref={sentinelRef} data-testid="product-list-sentinel" sx={{ height: 1 }} />}
    </Box>
  );
};

export const PublicList = React.memo(ProductListComponent);
